import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import 'moment-timezone';
import lodash from 'lodash';

import { removeNonASCIIChars } from 'pages/delegationOfAuthority/util/removeNonASCIICharsforExport';
import { getTzAbbreviation } from '../../../../common/util/dateTime';

const DATE_FORMAT = 'MMM DD YYYY';
const DATE_FORMAT_NON_COMMA = 'DD MMM YYYY';
const DATE_NOT_TZ_FORMAT = 'MMM DD YYYY, hh:mm A';
const MINOR_SPACING_MM = 2;
const NOT_APPLICABLE = 'N/A';

const ACTIVITY_ADD_TEAM_MEMBER = 'A corresponding delegation is created when a Study Team Member is added in Study Settings.';
const ACTIVITY_UPDATE_TEAM_MEMBER = 'Updating this Study Team Member’s role in Study Settings updates this user’s start date.';
const ACTIVITY_REMOVE_TEAM_MEMBER = 'Removing this Study Team Member in Study Settings updates this user’s end date.';

const ORIGIN_REMOVE_ADD_TEAM_MEMBER = 'Study Settings';
const ORIGIN_EDIT_CANCEL_SIGNATURE = 'Delegation';

const DEFAULT_PI_TESTAMENT = `
I have delegated to each individual above the authority to perform the tasks indicated, under my supervision. 
As of the start date, each individual was qualified to perform the delegated task(s) on the basis of education,
training or experience. (See FDA guidance for Industry Investigator Responsibilities - Protecting the Rights, 
Safety, and Welfare of Study Subjects.)
Electronic Signature(s) Attached`;

const DEFAULT_STAFF_TESTAMENT = `
By signing, I confirm/acknowledge that I accept responsibility for and am appropriately trained, skilled and qualified for the tasks listed herein
Mechanisms are in place to ensure that I receive the appropriate information and training throughout the study and that a 2-way communication channel
exists between P.I. and self.
Electronic Signature(s) Attached`;

const summaryLogCellsSize = {
    0: { cellWidth: 40 },
    1: { cellWidth: 40 },
    2: { cellWidth: 30 },
    3: { cellWidth: 30 },
    4: { cellWidth: 30 },
    5: { cellWidth: 60 },
    6: { cellWidth: 30 },
};
const summaryLogSignedCellsSize = {
    0: { cellWidth: 40 },
    1: { cellWidth: 40 },
    2: { cellWidth: 30 },
    3: { cellWidth: 30 },
    4: { cellWidth: 30 },
    5: {
        cellWidth: 17,
        halign: 'right',
    },
    6: { cellWidth: 60 },
    7: { cellWidth: 30 },
};

const doaActionMapping = {
    CREATE_DELEGATION: {
        identifier: 'CREATE_DELEGATION',
        text: 'Created Delegation'
    },
    EDIT_DELEGATION: {
        identifier: 'EDIT_DELEGATION',
        text: 'Modified Delegation'
    },
    SIGN_DELEGATION_END_DATE_AS_PI: {
        identifier: 'SIGN_DELEGATION_END_DATE_AS_PI',
        text: 'Signed Delegation'
    },
    SIGN_DELEGATION_START_DATE_AS_PI: {
        identifier: 'SIGN_DELEGATION_START_DATE_AS_PI',
        text: 'Signed Delegation'
    },
    SIGN_DELEGATION_START_DATE_AS_STAFF: {
        identifier: 'SIGN_DELEGATION_START_DATE_AS_STAFF',
        text: 'Signed Delegation'
    },
    SIGN_DELEGATION_AS_PI: {
        identifier: 'SIGN_DELEGATION_AS_PI',
        text: 'Signed Delegation'
    },
    SIGN_DELEGATION_AS_STAFF: {
        identifier: 'SIGN_DELEGATION_AS_STAFF',
        text: 'Signed Delegation'
    }
};

class DoAExportV2 {
    constructor(timezoneOffset, timezone, doaStudyTasks, doaStudyTasksForExport, doaStudyDelegations, binder, testaments, doaAuditHistory, doaRoles, __, studyId, isCentralizedInstitution, studyMetadata, selectedDatasourceOverrides) {
        this.timezoneOffset = timezoneOffset;
        this.timezone = timezone;
        this.doaStudyTasks = doaStudyTasks;
        this.doaStudyTasksForExport = doaStudyTasksForExport;
        this.doaStudyDelegations = doaStudyDelegations;
        this.binder = binder;
        this.testaments = testaments;
        this.doaAuditHistory = doaAuditHistory;
        this.doaRoles = doaRoles;
        this.studyId = studyId;
        this.isCentralizedInstitution = isCentralizedInstitution;
        this.studyMetadata = studyMetadata;
        this.selectedDatasourceOverrides = selectedDatasourceOverrides;
    }

    formatTimestamp(date, format) {
        return moment.utc(date).utcOffset(this.timezoneOffset).format(format);
    }

    formatDate(date) {
        if (lodash.isNil(date)) {
            return null;
        }

        return moment.utc(date).format(DATE_FORMAT);
    }

    formatDateWithInstitutionalTZ(date) {
        const timezone = this.timezone || moment.tz.guess();
        const time = moment.utc(date).tz(timezone).format(DATE_NOT_TZ_FORMAT);
        const timezoneAbbreviation = getTzAbbreviation(date, timezone);

        return `${time} - ${timezoneAbbreviation}`;
    }

    //part 1
    writePagingHeaders(pdf, exportDate) {
        pdf.setFontSize(8);

        const { fontName } = pdf.getFont();
        pdf.setFont(fontName, 'bold');

        let pageNumber = pdf.internal.getNumberOfPages();

        for (let currentPage = 1; currentPage <= pageNumber; currentPage++) {
            pdf.setPage(currentPage);

            pdf.text(`Page ${currentPage} of ${pageNumber} | Generated by Complion ${exportDate}`, 200, 7);
        }

        pdf.setFontSize(12);
    }

    //part 2
    writeDoAHeader(pdf, currentPdfLocation, margin) {
        pdf.autoTable({
            startY: currentPdfLocation + 5,
            margin: { horizontal: margin },
            head: [[this.binder.title]],
            body: [['Delegation of Authority Log']],
            didParseCell: function (table) {
                table.cell.styles.fillColor = '#F4F5F7';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 16;
                table.cell.styles.cellPadding = 3;
                if (table.section === 'head') {
                    table.cell.styles.textColor = [41, 128, 185];
                    table.cell.styles.fontSize = 14;
                }
            }
        });
        return currentPdfLocation + 30;
    }

    //part 3
    writeStudySummary(pdf, currentPdfLocation, margin) {
        pdf.autoTable({
            startY: currentPdfLocation + MINOR_SPACING_MM,
            margin: { horizontal: margin },
            head: null,
            body: [['Study Summary']],
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#FFFFFF';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
                table.cell.styles.cellPadding = 3;
            },
        });
        const columns = [['Principal Investigator', 'Protocol/Study Number', 'Sponsor', 'Site Number']];

        //Check for Datasource overrides to find associated values for corresponding columns
        const piOverrideName = this.selectedDatasourceOverrides.find(o => o.label === 'Principal Investigator');
        const piDataSourceName = piOverrideName ? piOverrideName.dataSourceName : 'primaryInvestigator';

        const trailIDOverrideName = this.selectedDatasourceOverrides.find(o => o.label === 'Protocol Number / Trial id');
        const trailIDDataSourceName = trailIDOverrideName ? trailIDOverrideName.dataSourceName : 'trialId';

        const sponsorOverrideName = this.selectedDatasourceOverrides.find(o => o.label === 'Sponsor');
        const sponsorDataSourceName = sponsorOverrideName ? sponsorOverrideName.dataSourceName : 'sponsor';

        const siteOverrideName = this.selectedDatasourceOverrides.find(o => o.label === 'Site Number');
        const siteDataSourceName = siteOverrideName ? siteOverrideName.dataSourceName : 'siteNumber';

        const isMetadataAvailable = !lodash.isEmpty(this.studyMetadata);

        const row = {
            principalInvestigator: (isMetadataAvailable ? lodash.get(
                this.studyMetadata.find(m => m.name === piDataSourceName), 'value', ''
            ) : '') || this.binder.primaryInvestigator,

            studyNumber: (isMetadataAvailable ? lodash.get(
                this.studyMetadata.find(m => m.name === trailIDDataSourceName), 'value', ''
            ) : '') || this.binder.trialId,

            sponsor: (isMetadataAvailable ? lodash.get(
                this.studyMetadata.find(m => m.name.toLowerCase() === sponsorDataSourceName.toLowerCase()), 'value', ''
            ) : '') || this.binder.sponsorCro,

            site: (isMetadataAvailable ? lodash.get(
                this.studyMetadata.find(m => m.name === siteDataSourceName), 'value', ''
            ) : '') || this.binder.departmentSiteDg,
        };

        let rows = [];
        rows.push([row.principalInvestigator, row.studyNumber, row.sponsor, row.site]);
        pdf.autoTable({
            startY: currentPdfLocation + 10,
            margin: { horizontal: margin },
            head: columns,
            body: rows,
            theme: 'plain',
            didParseCell: function (table) {
                table.cell.styles.cellPadding = 3;
                table.cell.styles.fontStyle = 'bold';
                if (table.cell.section === 'head') {
                    table.cell.styles.fontStyle = 'normal';
                }
            },
        });

        return currentPdfLocation + 30;
    }

    //part 4
    writeTaskList(pdf, currentPdfLocation, margin) {
        currentPdfLocation += 5;

        pdf.autoTable({
            startY: currentPdfLocation,
            margin: { horizontal: margin },
            head: null,
            body: [['Task List']],
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#FFFFFF';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
                table.cell.styles.cellPadding = 3;
            },
        });

        currentPdfLocation += 10;

        const studyTasks = this.doaStudyTasks;
        const thirdListLength = Math.ceil(studyTasks.length / 3);
        const leftMaxRange = thirdListLength;
        const middleMaxRange = (thirdListLength * 2);
        const rows = [];

        for (let i = 0; i < thirdListLength; i++) {
            rows.push([
                `${studyTasks[i] ? `${studyTasks[i].taskIndex}.` : ''} ${studyTasks[i] ? removeNonASCIIChars(studyTasks[i].taskName) : ''}`,
                `${studyTasks[i + leftMaxRange] ? `${studyTasks[i + leftMaxRange].taskIndex}.` : ''} ${studyTasks[i + leftMaxRange] ? removeNonASCIIChars(studyTasks[i + leftMaxRange].taskName) : ''}`,
                `${studyTasks[i + middleMaxRange] ? `${studyTasks[i + middleMaxRange].taskIndex}.` : ''} ${studyTasks[i + middleMaxRange] ? removeNonASCIIChars(studyTasks[i + middleMaxRange].taskName) : ''}`,
            ]);
        }

        pdf.autoTable({
            startY: currentPdfLocation,
            showHead: false,
            columnStyles: {
                0: { cellWidth: 80 },
                1: { cellWidth: 80 },
                2: { cellWidth: 80 },
            },
            body: rows,
            theme: 'plain',
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'normal';
                table.cell.styles.fillColor = '#FFFFFF';
                table.cell.styles.textColor = 'black';
            },
        });
        return currentPdfLocation + (thirdListLength * 10);
    }

    //part 5
    writeDelegationSummaryLog(pdf, currentPdfLocation, margin) {
        currentPdfLocation += 5;
        pdf.autoTable({
            margin: {
                top: margin,
                left: margin,
                right: margin,
            },
            head: null,
            body: [['Delegation Summary Log']],
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#FFFFFF';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
                table.cell.styles.cellPadding = 3;
            },
        });

        let rows = [];

        const isExistedSignature = lodash.some(this.doaStudyDelegations, doa => !lodash.isEmpty(doa.signatures));
        const columns = isExistedSignature ? [['Name', 'Role', 'Tasks', 'Start Date', 'End Date', ' ', 'Signed By', 'Signed Date']] : [['Name', 'Role', 'Tasks', 'Start Date', 'End Date', 'Signed By', 'Signed Date']];

        const getPISignatureBySignatureType = (signatures, signatureType, PIUserId = null) => {
            if (!lodash.isNil(PIUserId)) {
                return signatures.filter(signature => signature.signatureType === signatureType && lodash.get(signature, 'signature.user.id', '') === PIUserId);
            } else {
                return signatures.filter(signature => signature.signatureType === signatureType);
            }
        };

        const getSignedSignatures = (signatures) => {
            if (signatures.length === 0) {
                return [];
            }
            const signedSignatures = signatures.filter(signedSignature => signedSignature.signature);
            if (signedSignatures.length === 0) {
                return [];
            }

            const sortedSignatures = lodash.orderBy(signedSignatures, 'signature.createdAt', 'desc');
            const staffStart = lodash.head(sortedSignatures.filter(signature => signature.signatureType === 'STAFF_START'));
            const piStartSignatures = getPISignatureBySignatureType(sortedSignatures, 'PI_START', null);
            const piEndSignatures = getPISignatureBySignatureType(sortedSignatures, 'PI_END', null);
            const mostRecentPIStartSignature = lodash.head(piStartSignatures);
            const mostRecentPIEndSignature = lodash.head(piEndSignatures);

            const mostRecentPISignature = lodash.chain([mostRecentPIStartSignature, mostRecentPIEndSignature])
                .compact()
                .orderBy('signature.createdAt', 'desc')
                .head()
                .value();

            return lodash.chain([staffStart, mostRecentPISignature])
                .compact()
                .orderBy('signature.createdAt', 'asc')
                .value();
        };

        const buildSignedBy = (signatures) => {
            if (signatures.length === 0) {
                return '';
            }
            return signatures.map(signature => {
                return `${signature.signature.user.displayName}`;
            }).join('\n');
        };

        const buildSignedDate = (signatures) => {
            if (signatures.length === 0) {
                return '';
            }
            return signatures.map(signature => {
                return signature.signature && this.formatTimestamp(signature.signature.createdAt, DATE_FORMAT);
            }).join('\n');
        };

        const buildLabel = (signatures) => {
            if (signatures.length === 0) {
                return '';
            }
            return signatures.map(signature => {
                const role = signature.signatureType === 'PI' ||
                    signature.signatureType === 'PI_START' ||
                    signature.signatureType === 'PI_END' ? 'P.I.' : 'STAFF';

                return `${role}`;
            }).join('\n');
        };

        this.doaStudyDelegations.forEach(delegation => {
            const signatures = getSignedSignatures(delegation.signatures);
            rows.push([
                delegation.user.displayName,
                delegation.role ? delegation.role.name : '',
                delegation.tasks ? delegation.tasks.map(task => task.taskIndex).sort((a, b) => a - b).join(', ') : '',
                delegation.startDate ? this.formatDate(delegation.startDate) : '',
                delegation.endDate ? this.formatDate(delegation.endDate) : '',
                buildLabel(signatures),
                buildSignedBy(signatures),
                buildSignedDate(signatures)
            ]);
        });

        currentPdfLocation += 10;

        pdf.autoTable({
            rowPageBreak: 'avoid',
            margin: { horizontal: margin },
            head: columns,
            body: rows,
            columnStyles: isExistedSignature ? summaryLogSignedCellsSize : summaryLogCellsSize,

            didParseCell: function ({ cell, column }) {
                cell.styles.cellPadding = 3;
                cell.styles.fontStyle = 'normal';
                cell.styles.textColor = [0, 0, 0];
                cell.styles.fontSize = 9;
                if (cell.section === 'head') {
                    cell.styles.fillColor = [255, 255, 255];
                    cell.styles.textColor = [0, 0, 0];
                    cell.styles.fontStyle = 'bold';
                    cell.styles.fontSize = 9;
                }
                if (column.title === ' ') {
                    cell.styles.fontStyle = 'italic';
                }
            },
        });

        return currentPdfLocation + (lodash.get(this.doaStudyDelegations, 'length', 0) * 5);
    }

    //part 6
    writeStatementOfTestament(pdf, currentPdfLocation, margin) {
        pdf.addPage();

        const testaments = [
            {
                title: 'Principal Investigator Testament',
                statement: this.getStatementOfTestament('PI')
            },
            {
                title: 'Staff Testament',
                statement: this.getStatementOfTestament('Staff')
            }
        ];

        testaments.map(testament => {
            const titleRow = [[testament.title]];
            const contentRow = [[testament.statement]];
            pdf.autoTable({
                margin: { horizontal: margin },
                head: titleRow,
                body: contentRow,
                theme: 'plain',
                headStyles: {
                    fontStyle: 'bold',
                    fillColor: '#F4F5F7',
                    textColor: 'black',
                    fontSize: 14,
                    cellPadding: 3
                }
            });
        });

        return currentPdfLocation;
    }

    getMatchingTestament(testaments, title) {
        return lodash.find(testaments, testament => testament.title.toUpperCase() === title.toUpperCase());
    }

    getStatementOfTestament(title) {
        if (this.testaments && this.getMatchingTestament(this.testaments, title)) {
            const testament = this.getMatchingTestament(this.testaments, title);

            if (title.toUpperCase() === 'STAFF' && this.isCentralizedInstitution) {
                return `${NOT_APPLICABLE}`;
            }

            return removeNonASCIIChars(testament.text);
        }

        if (title.toUpperCase() === 'PI') {
            return DEFAULT_PI_TESTAMENT;
        }
        else if (this.isCentralizedInstitution) {
            return `
            ${NOT_APPLICABLE}`;
        }
        return DEFAULT_STAFF_TESTAMENT;
    }

    //part 7
    writeDelegationAuditLogs(pdf, currentPdfLocation, margin) {
        pdf.addPage();

        currentPdfLocation = 10;

        pdf.autoTable({
            startY: currentPdfLocation,
            margin: { horizontal: margin },
            head: null,
            body: [['Delegation Audit Logs']],
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#FFFFFF';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
                table.cell.styles.cellPadding = 3;
            },
        });

        const splitText = text => {
            const arrayText = pdf.splitTextToSize(text, 85);
            if (arrayText.length > 1) {
                return arrayText.join('\n\t\t\t\t');
            }
            return arrayText;
        };

        const self = this;

        const buildCreatedDelegationHistory = (userName, jsonValue) => {
            const role = self.doaRoles.find(role => role.id === jsonValue.roleManagementId);
            const createHistoryRecord = [];

            const nameRender = `NAME:    \t\t${splitText(userName)}`;
            const roleRender = role ? `ROLE:     \t\t${splitText(role.name)}` : '';
            const taskRender = !lodash.isEmpty(jsonValue.tasks) ? `TASKS:   \t\t${jsonValue.tasks.map(task => splitText(task.taskName)).join('\n\t\t\t\t')}` : '';
            const startDateRender = !lodash.isEmpty(jsonValue.startDate) ? `START DATE:\t${splitText(self.formatDate(jsonValue.startDate))}` : '';
            const endDateRender = !lodash.isEmpty(jsonValue.endDate) ? `END DATE:    \t${splitText(self.formatDate(jsonValue.endDate))}` : '';

            const signatureExpectationsRender = !lodash.isEmpty(jsonValue.signatureExpectations) ?
                `REQUESTED SIGNATURES:\n\t\t\t\t${lodash
                    .chain(jsonValue.signatureExpectations)
                    .filter(signature => signature.delegationId === jsonValue.id)
                    .map(signature => splitText(signature.user.name))
                    .join('\n\t\t\t\t')
                    .value()}` : '';

            const defaultSignatureExpectationsRender = !lodash.isEmpty(jsonValue.defaultSignatureExpectations) ?
                `ORIGINAL SIGNATURES:\n\t\t\t\t${lodash
                    .chain(jsonValue.defaultSignatureExpectations)
                    .filter(signature => signature.delegationId === jsonValue.id)
                    .map(signature => splitText(signature.user.name))
                    .join('\n\t\t\t\t')
                    .value()}` : '';

            if (nameRender) {
                createHistoryRecord.push(nameRender);
            }
            if (roleRender) {
                createHistoryRecord.push(roleRender);
            }
            if (taskRender) {
                createHistoryRecord.push(taskRender);
            }
            if (startDateRender) {
                createHistoryRecord.push(startDateRender);
            }
            if (endDateRender) {
                createHistoryRecord.push(endDateRender);
            }
            if (defaultSignatureExpectationsRender) {
                createHistoryRecord.push(defaultSignatureExpectationsRender);
            }
            if (signatureExpectationsRender) {
                createHistoryRecord.push(signatureExpectationsRender);
            }

            return createHistoryRecord.join('\n\n');
        };

        const buildEditDelegationHistory = (__, currentAuditValue, previousAuditValue) => {
            let roleRemove,
                roleAdd,
                taskRemove = [],
                taskAdd = [],
                startDateRemove,
                startDateAdd,
                endDateRemove,
                endDateAdd,
                signatureRemove = [],
                signatureAdd = [];

            // SRB-874, in DoA v2 `signatureExpectations` field was added to audit history, 
            // Delegations that were created in v1 do not have this field in their audit history
            previousAuditValue.signatureExpectations = previousAuditValue.signatureExpectations || [];

            const preRoleId = previousAuditValue.roleManagementId;
            const curRoleId = currentAuditValue.roleManagementId;

            if (preRoleId !== curRoleId) {
                let roleExists = self.doaRoles.find(role => role.id === preRoleId);

                roleRemove = roleExists ? splitText(roleExists.name) : '';
                roleExists = self.doaRoles.find(role => role.id === curRoleId);
                roleAdd = roleExists ? splitText(roleExists.name) : '';
            }

            // in DoA v2, audit records have both task id and name, but in v1 they only have task id
            // in v2 doaAuditTask is an object with id and name properties
            // in v1 doaAuditTask is a string and it is task id
            const compareDoaAuditTasks = (curDoaAuditTask, preDoaAuditTask) => {
                return (curDoaAuditTask.id ? curDoaAuditTask.id : curDoaAuditTask) ===
                    (preDoaAuditTask.id ? preDoaAuditTask.id : preDoaAuditTask);
            };

            const addNamesToDeletedVersionOneTasks = json => {
                if (json.tasks)
                    return json.tasks;

                if (json.taskIds) {
                    return json.taskIds.map(taskId => {
                        return this.doaStudyTasksForExport.find(task => task.id === taskId);
                    });
                }
            };

            const preTasks = addNamesToDeletedVersionOneTasks(previousAuditValue);
            const currentTasks = addNamesToDeletedVersionOneTasks(currentAuditValue);

            if (!lodash.isEqual(preTasks, currentTasks)) {
                preTasks && preTasks.forEach(preTask => {
                    const isExisted = lodash.some(currentTasks, task => compareDoaAuditTasks(task, preTask));
                    if (!isExisted) {
                        taskRemove.push(splitText(preTask.taskName));
                    }
                });

                currentTasks && currentTasks.forEach(curTask => {
                    const isExisted = lodash.some(preTasks, task => compareDoaAuditTasks(task, curTask));
                    if (!isExisted) {
                        taskAdd.push(splitText(curTask.taskName));
                    }
                });
            }

            const taskAddJoin = taskAdd.join('\n\t\t\t\t');
            const taskRemoveJoin = taskRemove.join('\n\t\t\t\t');

            const preStartDate = previousAuditValue.startDate;
            const curStartDate = currentAuditValue.startDate;

            if (preStartDate !== curStartDate) {
                if (!lodash.isEmpty(previousAuditValue.startDate)) {
                    startDateRemove = splitText(self.formatDate(previousAuditValue.startDate));
                }
                if (!lodash.isEmpty(currentAuditValue.startDate)) {
                    startDateAdd = splitText(self.formatDate(currentAuditValue.startDate));
                }
            }

            if (currentAuditValue.activity === ACTIVITY_UPDATE_TEAM_MEMBER) {
                if (!lodash.isEmpty(previousAuditValue.startDate)) {
                    startDateRemove = splitText(self.formatDate(previousAuditValue.startDate));
                }
                if (!lodash.isEmpty(currentAuditValue.startDate)) {
                    startDateAdd = splitText(self.formatDate(currentAuditValue.startDate));
                }
            }

            const preEndDate = previousAuditValue.endDate;
            const curEndDate = currentAuditValue.endDate;

            if (preEndDate !== curEndDate) {
                if (!lodash.isEmpty(previousAuditValue.endDate)) {
                    endDateRemove = splitText(self.formatDate(previousAuditValue.endDate));
                }
                if (!lodash.isEmpty(currentAuditValue.endDate)) {
                    endDateAdd = splitText(self.formatDate(currentAuditValue.endDate));
                }
            }

            if (currentAuditValue.activity === ACTIVITY_REMOVE_TEAM_MEMBER) {
                if (!lodash.isEmpty(previousAuditValue.endDate)) {
                    endDateRemove = splitText(self.formatDate(previousAuditValue.endDate));
                }
                if (!lodash.isEmpty(currentAuditValue.endDate)) {
                    endDateAdd = splitText(self.formatDate(currentAuditValue.endDate));
                }
            }

            if (!lodash.isEqual(previousAuditValue.signatureExpectations, currentAuditValue.signatureExpectations)) {

                const preSigs = previousAuditValue.signatureExpectations ?
                    lodash.chain(previousAuditValue.signatureExpectations)
                        .filter(signature => signature.delegationId === previousAuditValue.id)
                        .uniqWith(lodash.isEqual)
                        .value() : '';

                const currentSigs = currentAuditValue.signatureExpectations ?
                    lodash.chain(currentAuditValue.signatureExpectations)
                        .filter(signature => signature.delegationId === currentAuditValue.id)
                        .uniqWith(lodash.isEqual)
                        .value() : '';

                preSigs && preSigs.forEach(preSig => {
                    const isExisted = lodash.some(currentSigs, sig => sig.user.id === preSig.user.id);

                    if (!isExisted) {
                        signatureRemove.push(splitText(preSig.user.name));
                    }
                });

                currentSigs && currentSigs.forEach(currentSig => {
                    const isExisted = lodash.some(preSigs, sig => sig.user.id === currentSig.user.id);
                    if (!isExisted) {
                        signatureAdd.push(splitText(currentSig.user.name));
                    }
                });
            }

            const signatureRemoveJoin = signatureRemove && signatureRemove.join('\n\t\t\t\t');
            const signatureAddJoin = signatureAdd && signatureAdd.join('\n\t\t\t\t');
            const signatureRemoveRender = signatureRemoveJoin ? `SIGNATURES:\t${signatureRemoveJoin}` : '';
            const signatureAddRender = signatureAddJoin ? `SIGNATURES:\t${signatureAddJoin}` : '';
            const roleRemoveRender = roleRemove ? `ROLE:     \t\t${roleRemove}` : '';
            const roleAddRender = roleAdd ? `ROLE:     \t\t${roleAdd}` : '';
            const taskRemoveRender = taskRemoveJoin ? `TASKS:   \t\t${(taskRemoveJoin)}` : '';
            const taskAddRender = taskAddJoin ? `TASKS:   \t\t${(taskAddJoin)}` : '';
            const startDateRemoveRender = startDateRemove ? `START DATE:\t${startDateRemove}` : '';
            const startDateAddRender = startDateAdd ? `START DATE:\t${startDateAdd}` : '';
            const endDateRemoveRender = endDateRemove ? `END DATE:    \t${endDateRemove}` : '';
            const endDateAddRender = endDateAdd ? `END DATE:    \t${endDateAdd}` : '';

            const conditionSignatureRender = (signatureRemoveRender || signatureAddRender) &&
                (
                    roleRemoveRender ||
                    roleAddRender ||
                    taskRemoveRender ||
                    taskAddRender ||
                    startDateRemoveRender ||
                    startDateAddRender ||
                    endDateRemoveRender ||
                    endDateAddRender
                );

            const conditionSignatureRemoveRender = signatureRemoveRender &&
                !(
                    roleRemoveRender ||
                    roleAddRender ||
                    taskRemoveRender ||
                    taskAddRender ||
                    startDateRemoveRender ||
                    startDateAddRender ||
                    endDateRemoveRender ||
                    endDateAddRender
                );

            const conditionSignatureAddRender = signatureAddRender &&
                !(
                    roleRemoveRender ||
                    roleAddRender ||
                    taskRemoveRender ||
                    taskAddRender ||
                    startDateRemoveRender ||
                    startDateAddRender ||
                    endDateRemoveRender ||
                    endDateAddRender
                );

            let signatureExpectations;

            if (currentAuditValue.origin === 'Setup Study Tasks') {
                const setupStudyTasksSignatures = currentAuditValue.signatureExpectations.map((s) => {
                    return splitText(s.user.name);
                });

                signatureExpectations = !lodash.isEmpty(currentAuditValue.signatureExpectations) ?
                    `Requested Signatures\n\nRECIPIENT:    \t${lodash.join(setupStudyTasksSignatures, '\n\t\t\t\t')}` : '';

            } else {
                signatureExpectations = ((!signatureRemoveRender && !signatureAddRender) || conditionSignatureRender) &&
                    !lodash.isEmpty(currentAuditValue.signatureExpectations) ?
                    `Requested Signatures\n\nRECIPIENT:    \t${lodash
                        .chain(currentAuditValue.signatureExpectations)
                        .filter(signature => signature.delegationId === currentAuditValue.id)
                        .map(signature => splitText(signature.user.name))
                        .join('\n\t\t\t\t')
                        .value()}` : '';
            }

            const defaultSignatureExpectations = ((!(signatureRemoveRender || signatureAddRender) &&
                !(currentAuditValue.origin === 'Setup Study Tasks' ||
                    currentAuditValue.activity === ACTIVITY_UPDATE_TEAM_MEMBER ||
                    currentAuditValue.activity === ACTIVITY_REMOVE_TEAM_MEMBER)) || conditionSignatureRender) &&
                !lodash.isEmpty(currentAuditValue.defaultSignatureExpectations) ?
                `Original Signatures\n\nEXPECTATION:      ${lodash
                    .chain(currentAuditValue.defaultSignatureExpectations)
                    .filter(signature => signature.delegationId === currentAuditValue.id)
                    .map(signature => splitText(signature.user.name))
                    .join('\n\t\t\t\t')
                    .value()}` : '';

            const removedTitle =
                `${conditionSignatureRemoveRender || roleRemoveRender || taskRemoveRender || startDateRemoveRender || endDateRemoveRender ? 'Removed' : ''}`;

            const addedTitle =
                `${conditionSignatureAddRender || roleAddRender || taskAddRender || startDateAddRender || endDateAddRender ? 'Added' : ''}`;

            const editHistoryRecord = [];

            if (removedTitle) {
                editHistoryRecord.push(removedTitle);
                if (conditionSignatureRemoveRender) {
                    editHistoryRecord.push(signatureRemoveRender);
                }
                if (roleRemoveRender) {
                    editHistoryRecord.push(roleRemoveRender);
                }
                if (taskRemoveRender) {
                    editHistoryRecord.push(taskRemoveRender);
                }
                if (startDateRemoveRender) {
                    editHistoryRecord.push(startDateRemoveRender);
                }
                if (endDateRemoveRender) {
                    editHistoryRecord.push(endDateRemoveRender);
                }
            }

            if (addedTitle) {
                editHistoryRecord.push(addedTitle);
                if (conditionSignatureAddRender) {
                    editHistoryRecord.push(signatureAddRender);
                }
                if (roleAddRender) {
                    editHistoryRecord.push(roleAddRender);
                }
                if (taskAddRender) {
                    editHistoryRecord.push(taskAddRender);
                }
                if (startDateAddRender) {
                    editHistoryRecord.push(startDateAddRender);
                }
                if (endDateAddRender) {
                    editHistoryRecord.push(endDateAddRender);
                }
            }

            if (defaultSignatureExpectations && (currentAuditValue.origin !== 'Setup Study Tasks' && currentAuditValue.activity !== ACTIVITY_UPDATE_TEAM_MEMBER && currentAuditValue.activity !== ACTIVITY_REMOVE_TEAM_MEMBER)) {
                editHistoryRecord.push(defaultSignatureExpectations);
            }

            if (signatureExpectations && !lodash.isEmpty(currentAuditValue.tasks)) {
                editHistoryRecord.push(signatureExpectations);
            }

            return editHistoryRecord.join('\n\n');
        };

        const buildSignDelegationHistory = (userName, __, previousAuditValue) => {
            let role = self.doaRoles.find(role => role.id === previousAuditValue.roleManagementId);

            let signedHistoryRecord = [];
            const nameRender = `NAME:    \t\t${splitText(userName)}`;
            const roleRender = role ? `ROLE:     \t\t${splitText(role.name)}` : '';
            const taskRender = !lodash.isEmpty(previousAuditValue.tasks) ? `TASKS:   \t\t${previousAuditValue.tasks.map(task => splitText(task.taskName)).join('\n\t\t\t\t')}` : '';
            const startDateRender = !lodash.isEmpty(previousAuditValue.startDate) ? `START DATE:\t${splitText(self.formatDate(previousAuditValue.startDate))}` : '';
            const endDateRender = !lodash.isEmpty(previousAuditValue.endDate) ? `END DATE:    \t${splitText(self.formatDate(previousAuditValue.endDate))}` : '';

            if (nameRender) {
                signedHistoryRecord.push(nameRender);
            }
            if (roleRender) {
                signedHistoryRecord.push(roleRender);
            }
            if (taskRender) {
                signedHistoryRecord.push(taskRender);
            }
            if (startDateRender) {
                signedHistoryRecord.push(startDateRender);
            }
            if (endDateRender) {
                signedHistoryRecord.push(endDateRender);
            }

            return signedHistoryRecord.join('\n\n');
        };

        const buildHistoryDetails = (userName, actionType, current, previous) => {
            const currentAuditValue = JSON.parse(current);

            // For SRB-845, it was decided that potential failures resulting from the scenario where pre-2019 delegations had missing records would be treated in a fashion to "not fail" without making any real fixes.
            // Pass #1: fix for scenario where there was EDIT DELEGATION records without CREATE DELEGATION
            // Pass #2: fix for all situations missing a pre value.
            const initialAuditValue = {
                roleManagementId: undefined,
                signatureExpectations: [],
                tasks: [],
                startDate: undefined,
                endDate: undefined
            };

            const previousAuditValue = JSON.parse(previous) || initialAuditValue;

            let details = '';

            switch (actionType) {
                case doaActionMapping.CREATE_DELEGATION.identifier:
                    details = buildCreatedDelegationHistory(userName, currentAuditValue);
                    break;
                case doaActionMapping.EDIT_DELEGATION.identifier:
                    details = buildEditDelegationHistory(userName, currentAuditValue, previousAuditValue);
                    break;
                case doaActionMapping.SIGN_DELEGATION_END_DATE_AS_PI.identifier:
                case doaActionMapping.SIGN_DELEGATION_START_DATE_AS_PI.identifier:
                case doaActionMapping.SIGN_DELEGATION_START_DATE_AS_STAFF.identifier:
                case doaActionMapping.SIGN_DELEGATION_AS_PI.identifier:
                case doaActionMapping.SIGN_DELEGATION_AS_STAFF.identifier:
                    details = buildSignDelegationHistory(userName, currentAuditValue, previousAuditValue);
                    break;
                default:
                    break;
            }

            return details;
        };

        const buildActionDetails = (record) => {
            const jsonValue = JSON.parse(record.value);

            const lengthOfSignatureExpectations = jsonValue.signatureExpectations && jsonValue.signatureExpectations.length;
            const lengthOfDefaultSignatureExpectations = jsonValue.defaultSignatureExpectations && jsonValue.defaultSignatureExpectations.length;
            const isCancelSignature = lengthOfSignatureExpectations < lengthOfDefaultSignatureExpectations;

            const testament = jsonValue.testament ? `TESTAMENT:\n\n${jsonValue.testament}` : '';
            const originSigned = jsonValue.origin && this.studyId ? `\nORIGIN:\n\n${jsonValue.origin}` : '';
            let signedActionRecord = ['Signed Delegation'];
            if (testament) {
                signedActionRecord.push(testament);
            }
            if (originSigned) {
                signedActionRecord.push(originSigned);
            }

            const notes = record.notes ? `EXPLANATION:\n\n${record.notes}` : '';
            const activityEdit = jsonValue.activity && this.studyId ? `ACTIVITY:\n\n${jsonValue.activity}` : '';
            const originEdit = jsonValue.origin && this.studyId ? `ORIGIN:\n\n${jsonValue.origin}` : '';
            const originCancelSig = (!originEdit && (isCancelSignature && this.studyId) || (!jsonValue.origin && this.studyId)) ? `ORIGIN:\n\n${ORIGIN_EDIT_CANCEL_SIGNATURE}` : '';

            const editActionRecord = ['Modified Delegation'];

            if (activityEdit) {
                editActionRecord.push(activityEdit);
            }
            if (notes) {
                editActionRecord.push(notes);
            }
            if (originEdit) {
                editActionRecord.push(originEdit);
            }
            if (originCancelSig) {
                editActionRecord.push(originCancelSig);
            }

            switch (record.actionType) {
                case doaActionMapping.SIGN_DELEGATION_END_DATE_AS_PI.identifier:
                case doaActionMapping.SIGN_DELEGATION_START_DATE_AS_PI.identifier:
                case doaActionMapping.SIGN_DELEGATION_START_DATE_AS_STAFF.identifier:
                case doaActionMapping.SIGN_DELEGATION_AS_PI.identifier:
                case doaActionMapping.SIGN_DELEGATION_AS_STAFF.identifier:
                    return `${signedActionRecord.join('\n\n')}`;
                case doaActionMapping.EDIT_DELEGATION.identifier:
                    return `${editActionRecord.join('\n\n')}`;
                case doaActionMapping.CREATE_DELEGATION.identifier:
                    return `${doaActionMapping[record.actionType].text}
                            ${this.studyId ? `\nACTIVITY:\n\n${ACTIVITY_ADD_TEAM_MEMBER}` : ''}
                            ${this.studyId ? `\nORIGIN:\n\n${ORIGIN_REMOVE_ADD_TEAM_MEMBER}` : ''}`;
                default:
                    break;
            }
        };

        this.doaStudyDelegations.forEach((delegation, index) => {
            pdf.autoTable({
                margin: { horizontal: margin },
                head: delegation.endDate ? [['Staff', 'Role', 'Start Date', 'End Date']] : [['Staff', 'Role', 'Start Date', '                    ']],
                body: [[delegation.user.displayName, delegation.role ? delegation.role.name : '', delegation.startDate ? this.formatDate(delegation.startDate) : '', delegation.endDate ? this.formatDate(delegation.endDate) : '']],
                didParseCell: function ({ cell }) {
                    cell.styles.fillColor = [41, 128, 185];
                    cell.styles.textColor = 'white';
                    cell.styles.cellPadding = 3;
                    cell.styles.fontStyle = 'bold';
                }
            });

            currentPdfLocation += 30;

            const delegationHistory = this.doaAuditHistory[delegation.id];

            const filterRecordSigned = (delegationHistory, record, index) => {
                for (let i = index; i > 0; i--) {
                    const delegationActionType = delegationHistory[i - 1].actionType;
                    const create = delegationActionType.includes('CREATE');
                    const edit = delegationActionType.includes('EDIT');
                    const sign = delegationActionType.includes('SIGN_DELEGATION');

                    if (create || edit || sign) {
                        return buildHistoryDetails(delegation.user.displayName, record.actionType, record.value, i !== 0 ? delegationHistory[i - 1].value : null);
                    }
                }

                return buildHistoryDetails(delegation.user.displayName, record.actionType, record.value, index !== 0 ? delegationHistory[index - 1].value : null);
            };

            const rows = [];

            if (delegationHistory) {
                delegationHistory.map((record, index) => {
                    rows.push([
                        this.formatDateWithInstitutionalTZ(record.createdAt),
                        record.actionUserDisplayName,
                        buildActionDetails(record),
                        filterRecordSigned(delegationHistory, record, index),
                    ]);
                });

                pdf.autoTable({
                    margin: { horizontal: margin },
                    rowPageBreak: 'avoid',
                    head: [['Date and Time', 'User', 'Action', 'Delegation Details']],
                    body: rows,
                    styles: {
                        overflow: 'linebreak',
                    },
                    columnStyles: {
                        0: { cellWidth: 55 },
                        1: { cellWidth: 35 },
                        2: { cellWidth: 70 },
                        3: { cellWidth: 117 },
                    },
                    didParseCell: function ({ cell }) {
                        cell.styles.cellPadding = 3;
                        cell.styles.fontStyle = 'normal';
                        cell.styles.textColor = [0, 0, 0];
                        cell.styles.fontSize = 9;
                        if (cell.section === 'head') {
                            cell.styles.fillColor = [255, 255, 255];
                            cell.styles.textColor = [0, 0, 0];
                            cell.styles.fontStyle = 'bold';
                            cell.styles.fontSize = 9;
                            cell.styles.cellPadding = 3;
                        }
                    }
                });

                if (this.doaStudyDelegations.length - 1 !== index) {
                    pdf.addPage();
                }
            }
        });

        return currentPdfLocation;
    }

    exportDOALog() {
        const exportDate = moment().utcOffset(this.timezoneOffset).format(DATE_FORMAT_NON_COMMA);

        const margin = 10;
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
        });
        pdf.setFontSize(9);
        // eslint-disable-next-line no-unused-vars
        let currentPdfLocation = 5;
        currentPdfLocation = this.writeDoAHeader(pdf, currentPdfLocation, margin);
        currentPdfLocation = this.writeStudySummary(pdf, currentPdfLocation, margin);
        currentPdfLocation = this.writeTaskList(pdf, currentPdfLocation, margin);
        currentPdfLocation = this.writeDelegationSummaryLog(pdf, currentPdfLocation, margin);
        currentPdfLocation = this.writeStatementOfTestament(pdf, currentPdfLocation, margin);
        this.writeDelegationAuditLogs(pdf, currentPdfLocation, margin);

        this.writePagingHeaders(pdf, exportDate);
        const binderTitle = this.binder.title;
        const fileName = `${binderTitle}-delegation-log-${exportDate}.pdf`;
        pdf.save(fileName);
    }
}

export default DoAExportV2;
